import { useState, useEffect } from 'react';
import { usePage } from '@inertiajs/react';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  summary: string;
  published_at: string;
}

interface Props {
  onClose: () => void;
  onSelect: (post: { title: string; slug: string }) => void;
}

export default function PostPickerModal({ onClose, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch blog posts
    fetch('/admin/blog/posts/index_json.json')
      .then((res) => res.json())
      .then((data) => {
        setPosts(data.posts || []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const filteredPosts = posts.filter((post) =>
    post.title.toLowerCase().includes(search.toLowerCase()) ||
    post.summary?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (post: BlogPost) => {
    onSelect({ title: post.title, slug: post.slug });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500/75 dark:bg-black/75" onClick={onClose}></div>
        </div>

        <div className="inline-block align-bottom bg-background dark:bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle max-w-lg w-full">
          <div className="bg-background dark:bg-gray-900 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-foreground mb-4">
                  Insert Blog Post
                </h3>
                
                {/* Search */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search posts..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-lg focus:ring-primary focus:border-primary bg-background text-foreground"
                    autoFocus
                  />
                </div>

                {/* Posts List */}
                <div className="max-h-64 overflow-y-auto border border-border rounded-lg">
                  {loading ? (
                    <div className="p-4 text-center text-muted-foreground">Loading posts...</div>
                  ) : filteredPosts.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      {search ? 'No posts found' : 'No published posts available'}
                    </div>
                  ) : (
                    <ul className="divide-y divide-border">
                      {filteredPosts.map((post) => (
                        <li key={post.id}>
                          <button
                            type="button"
                            onClick={() => handleSelect(post)}
                            className="w-full px-4 py-3 text-left hover:bg-muted focus:outline-none focus:bg-muted"
                          >
                            <div className="font-medium text-foreground">
                              {post.title}
                            </div>
                            {post.summary && (
                              <div className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                {post.summary}
                              </div>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-muted dark:bg-gray-800 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
